// The department-segmented bar of the credits meter and the Credits screen: one segment per department that
// spent something this month, in production order, in its colour (docs/design/brand-direction/APP-SHELL-v2.md).
// Pure.
import { DEPARTMENT_LABEL, type Department, PRODUCTION_ORDER } from "../departments.ts";
import type { SpendDept } from "./sources.ts";

export interface Segment {
  dept: SpendDept;
  label: string;
  credits: number;
  /** the share of the whole bar, 0..1 */
  share: number;
}

export const spendLabel = (d: SpendDept): string => (d === "other" ? "Other" : DEPARTMENT_LABEL[d]);

/** Colour of a segment: a department's own; "other" belongs to none and is neutral. */
export const spendColor = (d: SpendDept): string => (d === "other" ? "var(--ink-4)" : `var(--dept-${d})`);

/** The departments that spent something, in production order, "other" last. */
export function spentDepartments(used: Partial<Record<SpendDept, number>>): SpendDept[] {
  const known = PRODUCTION_ORDER.filter((d: Department) => (used[d] ?? 0) > 0);
  return (used.other ?? 0) > 0 ? [...known, "other"] : known;
}

/**
 * The segments of the bar. `left` is the balance still to spend (the empty track at the end); null when there is
 * no balance (an unmetered studio: the bar is the split of the use alone).
 */
export function spendSegments(used: Partial<Record<SpendDept, number>>, left: number | null): Segment[] {
  const depts = spentDepartments(used);
  const total = depts.reduce((a, d) => a + (used[d] ?? 0), 0);
  const whole = total + Math.max(left ?? 0, 0);
  if (whole <= 0) return [];
  return depts.map((d) => ({
    dept: d,
    label: spendLabel(d),
    credits: used[d] ?? 0,
    share: (used[d] ?? 0) / whole,
  }));
}
